// Helpers for Report Builder share links.
// Shared report state lives in the URL hash; the portal lives in ?portal= so it
// survives email clients / Mimecast wrappers that strip fragments, and so
// recipients don't have to manually switch to US Spirits.

export function getHashParameters() {
  const params = {};
  const raw = (window.location.hash || "").replace(/^#/, "");
  if (!raw) return params;

  raw.split("&").forEach((param) => {
    if (!param) return;
    const eq = param.indexOf("=");
    const rawKey = eq === -1 ? param : param.slice(0, eq);
    const rawValue = eq === -1 ? "" : param.slice(eq + 1);
    let key;
    let value;
    try {
      key = decodeURIComponent(rawKey);
    } catch (e) {
      key = rawKey;
    }
    try {
      value = decodeURIComponent(rawValue);
    } catch (e) {
      value = rawValue;
    }
    try {
      params[key] = JSON.parse(value);
    } catch (e) {
      params[key] = value;
    }
  });

  return params;
}

export function encodeHashParameters(params) {
  return Object.keys(params || {})
    .filter((key) => params[key] !== undefined && params[key] !== null)
    .map((key) => {
      const value = params[key];
      const encodedValue =
        typeof value === "string"
          ? encodeURIComponent(value)
          : encodeURIComponent(JSON.stringify(value));
      return `${encodeURIComponent(key)}=${encodedValue}`;
    })
    .join("&");
}

export function setHashParameters(params, { history, replace = true } = {}) {
  const hash = encodeHashParameters(params);
  const next = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ""}`;
  if (history) {
    history.push(next);
  } else if (replace) {
    window.history.replaceState({ path: next }, "", next);
  } else {
    window.location.hash = hash;
  }
}

const SPIRITS_AXES = new Set(["Cocktail", "Flavor"]);

export function inferPortalFromLocation() {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("portal");
    if (fromQuery) return fromQuery;

    const hash = getHashParameters();
    if (hash.portal) return hash.portal;

    const axes = [hash.yaxis, hash.xaxis, hash.yaxis2, hash.xaxis2];
    if (axes.some((axis) => SPIRITS_AXES.has(axis))) {
      return "spiritsNew";
    }

    // Long cocktail filter links are spirits reports even when the hash was stripped.
    if (/(?:^|[?&])cocktail-component=/.test(window.location.search || "")) {
      return "spiritsNew";
    }
  } catch (e) {
    // ignore malformed URLs
  }
  return null;
}

// Recover as many complete entries as possible from a truncated JSON array string.
export function salvageJsonArray(raw, fallback = []) {
  if (raw == null || raw === "") return fallback;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (e) {
    // Keep walking backwards to the last complete quoted string / value.
    let candidate = String(raw).trim();
    for (let i = 0; i < 40 && candidate.length > 1; i++) {
      const cut = candidate.lastIndexOf(",");
      if (cut <= 0) break;
      candidate = candidate.slice(0, cut) + "]";
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch (err) {
        // keep trimming
      }
    }
    return fallback;
  }
}

export const COCKTAIL_TRENDS_HASH = {
  defaultActiveKey: "ReportBuilder",
  yaxis: "Cocktail",
  xaxis: "None",
  portal: "spiritsNew",
  fields: [
    ["accounts", "value"],
    ["placements", "value"],
    ["adds", "value"],
    ["change", "value"],
  ],
  number_shownY: 50,
};

export function buildCocktailTrendsUrl({ origin = window.location.origin } = {}) {
  return `${origin}/s?portal=spiritsNew#${encodeHashParameters(COCKTAIL_TRENDS_HASH)}`;
}

/**
 * Address-bar / email copies of "all cocktails selected" are enormous and get
 * truncated, which used to white-screen and now just dumps users on Competitive
 * Set. Rewrite those to a short Cocktail Report Builder link before Filters mount.
 */
export function normalizeOversizedCocktailShareUrl() {
  try {
    if (!/^\/s\/?$/.test(window.location.pathname)) return false;

    const search = window.location.search || "";
    if (!/(?:^|[?&])cocktail-component=/.test(search)) return false;

    const hash = getHashParameters();
    if (hash.defaultActiveKey === "ReportBuilder" || hash.yaxis === "Cocktail") {
      return false;
    }

    let cocktailRaw = "";
    try {
      cocktailRaw = new URLSearchParams(search).get("cocktail-component") || "";
    } catch (e) {
      cocktailRaw = "";
    }

    // URLSearchParams may fail on badly truncated query strings — fall back to regex.
    if (!cocktailRaw) {
      const match = search.match(/(?:^|[?&])cocktail-component=([^&]*)/);
      if (match) {
        try {
          cocktailRaw = decodeURIComponent(match[1]);
        } catch (e) {
          cocktailRaw = match[1];
        }
      }
    }

    const looksTruncated =
      cocktailRaw.length > 400 ||
      (cocktailRaw.startsWith("[") && !cocktailRaw.trim().endsWith("]"));

    let manyCocktails = looksTruncated;
    if (!manyCocktails) {
      const parsed = salvageJsonArray(cocktailRaw, []);
      manyCocktails = parsed.length >= 10;
    }

    if (!manyCocktails && !looksTruncated) return false;

    const url = new URL(window.location.href);
    url.search = "?portal=spiritsNew";
    url.hash = encodeHashParameters(COCKTAIL_TRENDS_HASH);
    window.history.replaceState({ path: url.toString() }, "", url.toString());
    return true;
  } catch (e) {
    console.warn("normalizeOversizedCocktailShareUrl failed", e);
    return false;
  }
}

export function buildReportShareUrl({
  portal,
  defaultActiveKey,
  href = window.location.href,
} = {}) {
  const url = new URL(href);

  // Always emit a short share link: portal only in query, report state in hash.
  // Long filter query strings are what email clients truncate.
  const reportHash = {
    ...getHashParameters(),
  };
  if (defaultActiveKey) {
    reportHash.defaultActiveKey = defaultActiveKey;
  }
  if (portal) {
    reportHash.portal = portal;
  }

  // If this looks like a cocktail report but axes weren't set, default them.
  if (
    reportHash.defaultActiveKey === "ReportBuilder" &&
    !reportHash.yaxis &&
    (portal === "spiritsNew" || reportHash.portal === "spiritsNew")
  ) {
    Object.assign(reportHash, COCKTAIL_TRENDS_HASH, reportHash);
  }

  url.search = "";
  if (portal || reportHash.portal) {
    url.searchParams.set("portal", portal || reportHash.portal);
  }
  url.hash = encodeHashParameters(reportHash);
  return url.toString();
}

export function upsertHashParam(key, value, { history } = {}) {
  const params = getHashParameters();
  params[key] = value;
  setHashParameters(params, { history, replace: !history });
}
